/**
 * TASK-PBG-002: TilingSprite Factory
 *
 * Factory function to create PIXI.TilingSprite instances from parallax layer configuration.
 * TilingSprites provide seamless scrolling by repeating the texture in both directions.
 *
 * @example
 * ```typescript
 * const sprite = createTilingSprite(
 *   config,
 *   texture,
 *   { minX: -250, maxX: 1250, minY: -200, maxY: 1000, width: 1500, height: 1200 }
 * );
 * ```
 */

import * as PIXI from 'pixi.js';
import type { ParallaxLayerConfig } from '../../types/terrain';
import type { BleedBounds } from './bleed-calculator';

/**
 * Create a PIXI.TilingSprite from parallax layer configuration
 *
 * The TilingSprite is sized to cover the entire bleed bounds and positioned
 * at the minimum coordinates. The texture repeats seamlessly in both directions.
 *
 * @param config - Parallax layer configuration
 * @param texture - PIXI texture to use for the sprite
 * @param bounds - Bleed bounds defining the sprite's coverage area
 * @returns Configured PIXI.TilingSprite ready for parallax updates
 *
 * @example
 * ```typescript
 * const farLayer = createTilingSprite(
 *   {
 *     textureUrl: '/assets/far-bg.png',
 *     parallaxFactor: 0.1,
 *     scale: 1.0,
 *     alpha: 0.8,
 *   },
 *   farTexture,
 *   bleedBounds
 * );
 * ```
 */
export function createTilingSprite(
  config: ParallaxLayerConfig,
  texture: PIXI.Texture,
  bounds: BleedBounds
): PIXI.TilingSprite {
  // Create TilingSprite with the provided texture and dimensions from bounds
  const sprite = new PIXI.TilingSprite({
    texture,
    width: bounds.width,
    height: bounds.height,
  });

  // Position sprite at the minimum coordinates of the bounds
  sprite.x = bounds.minX;
  sprite.y = bounds.minY;

  // Set alpha from config
  sprite.alpha = config.alpha;

  // Set tile scale - use custom tileScale if provided, otherwise use scale
  if (config.tileScale) {
    sprite.tileScale.set(config.tileScale.x, config.tileScale.y);
  } else {
    sprite.tileScale.set(config.scale, config.scale);
  }

  // Initialize tilePosition to 0,0 (will be updated by parallax updater)
  sprite.tilePosition.set(0, 0);

  return sprite;
}
